import { useEffect, useRef, useCallback } from 'react';
import activityLogger from '../utils/activityLogger';

/**
 * React Hook for Activity Logging
 * Automatically tracks component lifecycle and provides logging methods
 */
export const useActivityLogger = (componentName, initialData = {}) => {
  const mountTime = useRef(performance.now());
  const activityCount = useRef(0);
  const lastActivity = useRef(null);

  // Log component mount
  useEffect(() => {
    const logMount = async () => {
      await activityLogger.log(
        'component_lifecycle', 
        'component_mounted', 
        `Component ${componentName} mounted`,
        { 
          componentName, 
          mountTime: mountTime.current,
          initialData 
        }
      );
    };

    logMount();

    // Log component unmount
    return () => {
      const unmountTime = performance.now();
      const lifespan = unmountTime - mountTime.current;
      
      activityLogger.log(
        'component_lifecycle', 
        'component_unmounted', 
        `Component ${componentName} unmounted after ${Math.round(lifespan)}ms`,
        { 
          componentName, 
          lifespan: Math.round(lifespan),
          totalActivities: activityCount.current 
        }
      );
    };
  }, [componentName]);

  // Logging methods
  const logActivity = useCallback(async (action, details, metadata = {}) => {
    activityCount.current++;
    lastActivity.current = Date.now();
    
    return await activityLogger.log(
      'component_activity',
      action,
      `[${componentName}] ${details}`,
      {
        componentName,
        activityCount: activityCount.current,
        ...metadata
      }
    );
  }, [componentName]);

  const logUserAction = useCallback(async (action, details, metadata = {}) => {
    return await logActivity(`user_${action}`, details, { userAction: true, ...metadata });
  }, [logActivity]);

  const logStateChange = useCallback(async (stateName, oldValue, newValue) => {
    return await logActivity(
      'state_changed',
      `State ${stateName} changed: ${JSON.stringify(oldValue)} → ${JSON.stringify(newValue)}`,
      { stateName, oldValue, newValue }
    );
  }, [logActivity]);

  const logError = useCallback(async (error, context = '', additionalInfo = {}) => {
    return await activityLogger.logError(
      error,
      `${componentName}:${context}`,
      { componentName, ...additionalInfo }
    );
  }, [componentName]);

  const logPerformance = useCallback(async (metric, value, context = '') => {
    return await activityLogger.logPerformance(
      metric,
      value,
      `${componentName}:${context}`
    );
  }, [componentName]);

  // Specialized logging methods
  const logButtonClick = useCallback(async (buttonName, additionalData = {}) => {
    return await logUserAction(
      'button_click',
      `Button clicked: ${buttonName}`,
      { buttonName, ...additionalData }
    );
  }, [logUserAction]);

  const logFormSubmit = useCallback(async (formName, formData = {}) => {
    return await logUserAction(
      'form_submit',
      `Form submitted: ${formName}`,
      { formName, formData }
    );
  }, [logUserAction]);

  const logInputChange = useCallback(async (inputName, value, inputType = 'text') => {
    return await logUserAction(
      'input_change',
      `Input changed: ${inputName} (${inputType})`,
      { inputName, valueLength: value?.length || 0, inputType }
    );
  }, [logUserAction]);

  const logNavigation = useCallback(async (fromPage, toPage, method = 'click') => {
    return await logActivity(
      'navigation',
      `Navigation: ${fromPage} → ${toPage} (${method})`,
      { fromPage, toPage, method }
    );
  }, [logActivity]);

  const logModalOpen = useCallback(async (modalName, trigger = 'unknown') => {
    return await logUserAction(
      'modal_open',
      `Modal opened: ${modalName}`,
      { modalName, trigger }
    );
  }, [logUserAction]);

  const logModalClose = useCallback(async (modalName, method = 'unknown') => {
    return await logUserAction(
      'modal_close',
      `Modal closed: ${modalName}`,
      { modalName, method }
    );
  }, [logUserAction]);

  const logDataLoad = useCallback(async (dataType, recordCount, loadTime) => {
    return await logActivity(
      'data_load',
      `Data loaded: ${dataType} (${recordCount} records, ${loadTime}ms)`,
      { dataType, recordCount, loadTime }
    );
  }, [logActivity]);

  const logAPICall = useCallback(async (endpoint, method, statusCode, responseTime) => {
    return await logActivity(
      'api_call',
      `API call: ${method} ${endpoint} → ${statusCode} (${responseTime}ms)`,
      { endpoint, method, statusCode, responseTime }
    );
  }, [logActivity]);

  return {
    // Basic logging
    logActivity,
    logUserAction,
    logStateChange,
    logError,
    logPerformance,
    
    // Specialized logging
    logButtonClick,
    logFormSubmit,
    logInputChange,
    logNavigation,
    logModalOpen,
    logModalClose,
    logDataLoad,
    logAPICall,
    
    // Component stats
    getActivityCount: () => activityCount.current,
    getLastActivity: () => lastActivity.current,
    getLifespan: () => performance.now() - mountTime.current
  };
};

/**
 * Hook for tracking form activities
 */
export const useFormLogger = (formName, componentName = 'Form') => {
  const { logActivity, logUserAction, logError } = useActivityLogger(componentName);

  const logFormStart = useCallback(async () => {
    return await logActivity('form_start', `Form interaction started: ${formName}`);
  }, [logActivity, formName]);

  const logFieldFocus = useCallback(async (fieldName) => {
    return await logUserAction('field_focus', `Field focused: ${fieldName}`, { fieldName });
  }, [logUserAction]);

  const logFieldBlur = useCallback(async (fieldName, value) => {
    return await logUserAction('field_blur', `Field blurred: ${fieldName}`, { 
      fieldName, 
      hasValue: !!value,
      valueLength: value?.length || 0 
    });
  }, [logUserAction]);

  const logFieldChange = useCallback(async (fieldName, oldValue, newValue) => {
    return await logUserAction('field_change', `Field changed: ${fieldName}`, { 
      fieldName, 
      oldValueLength: oldValue?.length || 0,
      newValueLength: newValue?.length || 0 
    });
  }, [logUserAction]);

  const logFormValidation = useCallback(async (isValid, errors = []) => {
    return await logActivity('form_validation', `Form validation: ${isValid ? 'passed' : 'failed'}`, { 
      isValid, 
      errorCount: errors.length,
      errors 
    });
  }, [logActivity]);

  const logFormSubmit = useCallback(async (formData, success = true, errorMessage = null) => {
    const action = success ? 'form_submit_success' : 'form_submit_failed';
    const details = `Form submission ${success ? 'successful' : 'failed'}: ${formName}`;
    
    return await logActivity(action, details, { 
      formName,
      fieldCount: Object.keys(formData).length,
      success,
      errorMessage 
    });
  }, [logActivity, formName]);

  return {
    logFormStart,
    logFieldFocus,
    logFieldBlur,
    logFieldChange,
    logFormValidation,
    logFormSubmit
  };
};

/**
 * Hook for tracking chat activities
 */
export const useChatLogger = (componentName = 'Chat') => {
  const { logActivity, logUserAction, logError, logPerformance } = useActivityLogger(componentName);

  const logChatStart = useCallback(async (userId, userEmail) => {
    return await logActivity('chat_start', `Chat session started`, { userId, userEmail });
  }, [logActivity]);

  const logMessageSent = useCallback(async (message, messageId) => {
    return await logUserAction('message_sent', `Message sent: "${message.substring(0, 50)}..."`, { 
      messageId,
      messageLength: message.length,
      wordCount: message.split(' ').length 
    });
  }, [logUserAction]);

  const logMessageReceived = useCallback(async (message, provider, responseTime) => {
    return await logActivity('message_received', `AI response received from ${provider}`, { 
      provider,
      responseLength: message.length,
      responseTime,
      wordCount: message.split(' ').length 
    });
  }, [logActivity]);

  const logChatEnd = useCallback(async (messageCount, duration) => {
    return await logActivity('chat_end', `Chat session ended`, { messageCount, duration });
  }, [logActivity]);

  const logVoiceStart = useCallback(async () => {
    return await logUserAction('voice_start', 'Voice input started');
  }, [logUserAction]);

  const logVoiceEnd = useCallback(async (transcript, duration) => {
    return await logUserAction('voice_end', 'Voice input ended', { 
      transcriptLength: transcript?.length || 0,
      duration 
    });
  }, [logUserAction]);

  return {
    logChatStart,
    logMessageSent,
    logMessageReceived,
    logChatEnd,
    logVoiceStart,
    logVoiceEnd
  };
};

export default useActivityLogger; 